/**
 * @param {Theme} theme
 * @param {BgColorStyleType} style
 * @returns {string}
 */
export const getFontColor = (theme, style) => {
  if (style === 'GRAY') {
    return theme.designTokens.colors.deepHumanBlue.deepHumanBlue100;
  }
  if (style === 'LIGHT') {
    return theme.designTokens.colors.deepHumanBlue.deepHumanBlue100;
  }
  return theme.color.white500;
};

/**
 * @param {Theme} theme
 * @param {BgColorStyleType} [backgroundStyle]
 * @returns {string}
 */
export const getBgColorByBgStyle = (theme, backgroundStyle) => {
  switch (backgroundStyle) {
    case 'GRAY':
      return theme.designTokens.colors.grey.grey10;
    case 'LIGHT':
      return theme.designTokens.colors.deepHumanBlue.deepHumanBlue30;
    case 'DARK':
      return theme.designTokens.colors.deepHumanBlue.deepHumanBlue100;
    case 'GRADIENT':
      return theme.color.gradientprimary500;
    default:
      return theme.color.primary600;
  }
};

/**
 * @param {Theme} theme
 * @param {string} key
 * @returns {string | undefined}
 */
function findKeyInTheme(theme, key) {
  if (typeof theme !== 'object' || theme === null) {
    return undefined;
  }

  /**
   * @type {string | undefined}
   */
  let result;
  Object.entries(theme).forEach(([currentKey, value]) => {
    if (result !== undefined) return;
    if (currentKey === key) {
      result = value;
    } else if (typeof value === 'object' && value !== null) {
      result = findKeyInTheme(value, key);
    }
  });
  return result;
}

/**
 * @param {Theme} theme
 * @param {ColorType | string} colorName
 * @returns {string}
 * @description Returns a hex color code based on the provided color name or hex code.
 * If the color name exists in the theme's color palette, it returns the corresponding hex code.
 * If a valid hex code is provided, it returns that code. If neither is valid, it returns a default
 * hex code.
 */
export const getColorByName = (theme, colorName) => {
  const hexRegex = new RegExp('^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$');
  if (theme.color[colorName]) {
    return theme.color[colorName];
  }
  if (theme?.designTokens?.colors) {
    const foundGroup = Object.values(theme.designTokens.colors).find(
      (group) =>
        group &&
        typeof group === 'object' &&
        Object.prototype.hasOwnProperty.call(group, colorName),
    );
    if (foundGroup) {
      return /** @type {any} */ (foundGroup)[colorName];
    }
  }

  const found =
    typeof findKeyInTheme === 'function'
      ? findKeyInTheme(theme, colorName)
      : undefined;
  if (typeof found === 'string') {
    return found;
  }
  if (hexRegex.test(colorName)) {
    return colorName;
  }
  return '#ff00cc';
};

/**
 * @param {Theme} theme
 * @param {ColorType | string} colorName
 * @returns {string | undefined}
 */
export const getThemeValueByName = (theme, colorName) => {
  return findKeyInTheme(theme, colorName);
};

/**
 * @param {ColorType} brandName
 * @returns {string}
 */
export const getCompanyColor = (brandName) => {
  const ciColors = {
    // ToDo: These colors aren't final. We need styleguides to check them.
    FSE: '#1b1655',
    FRESENIUS_SE: '#1b1655',
    FRESENIUS_MANUAL: '#1b1655',
    FRESENIUS_HEALTH_SERVICES: '#1b1655',
    GIJP_LARGE: '#1b1655',
    GIJP_FOOTER: '#1b1655',
    GIJP_MOBILE: '#1b1655',
    FMC: '#07238e',
    FRESENIUS_MEDICAL_CARE: '#07238e',
    KABI: '#0063be',
    FRESENIUS_KABI: '#0063be',
    FRESENIUS_HELIOS_QUIRONSALUD: '#1b1655',
    FRESENIUS_HELIOS_QUIRONPREVENCION: '#1b1655',
    FRESENIUS_HELIOS_HOSPITALS: '#00805F',
    VAMED: '#00A4C7',
    FRESENIUS_VAMED: '#00A4C7',
    HELIOS: '#00805F',
    FRESENIUS_HELIOS: '#00805F',
    NETCARE: '#225AA6',
    FRESENIUS_NETCARE: '#225AA6',
    CURALIE: '#4A63E7',
    FRESENIUS_CURALIE: '#4A63E7',
    MEDITEC: '#4A63E7',
    FRESENIUS_MEDITEC: '#4A63E7',
    FRESENIUS_DIGITAL_TECHNOLOGY: '#1b1655',
    NEPHROCARE: '#07238e',
    VIVONIC: '#07238e',
    XENIOS: '#07238e',
    DEFAULT: '#1b1655',
  };
  return ciColors[brandName] || '#ff00cc';
};
